/**
 * Product status options for price-list product details.
 * Values are stored on Item.status; titles are Romanian UI labels.
 * Custom free-text statuses are also allowed (stored and displayed as-is).
 */
export const PRODUCT_STATUS_OPTIONS = [
    { title: 'Stoc', value: 'stoc' },
    { title: 'Lichidare stoc', value: 'lichidare_stoc' },
    { title: 'Anulat', value: 'anulat' },
    { title: 'Pe comanda', value: 'pe_comanda' },
    { title: 'De verificat', value: 'de_verificat' },
];

/** Text color for status labels in tables / builder summary. */
export const PRODUCT_STATUS_COLORS = {
    stoc: '#2e7d32',
    lichidare_stoc: '#ef6c00',
    anulat: '#c62828',
    pe_comanda: '#1565c0',
    de_verificat: '#6a1b9a',
};

/**
 * Palette for free-text/custom statuses (stable color per label via hash).
 * Avoids clashing with the predefined status hues above.
 */
const CUSTOM_STATUS_COLOR_PALETTE = [
    '#6a1b9a', // purple
    '#00838f', // teal
    '#5d4037', // brown
    '#4527a0', // deep purple
    '#00695c', // green teal
    '#ad1457', // pink
    '#283593', // indigo
    '#558b2f', // light green
    '#6d4c41', // warm brown
    '#0277bd', // light blue
];

export const PRODUCT_STATUS_LABELS = Object.fromEntries(
    PRODUCT_STATUS_OPTIONS.map((option) => [option.value, option.title]),
);

const PRODUCT_STATUS_MAX_LENGTH = 64;

/**
 * Stable palette index for a custom status string.
 * @param {string} key
 * @returns {number}
 */
function hashStatusKey(key) {
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) {
        hash = ((hash << 5) - hash) + key.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash) % CUSTOM_STATUS_COLOR_PALETTE.length;
}

/**
 * Normalize combobox / select / free-text status to a stored string.
 * Known titles map to their values; unknown text is kept as custom status.
 * @param {unknown} value
 * @returns {string|null}
 */
export function normalizeProductStatus(value) {
    if (value == null || value === '') {
        return null;
    }
    let raw = value;
    if (typeof value === 'object') {
        raw = value.value ?? value.title ?? '';
    }
    const text = String(raw || '').trim();
    if (!text) {
        return null;
    }
    const byValue = PRODUCT_STATUS_OPTIONS.find((option) => option.value === text);
    if (byValue) {
        return byValue.value;
    }
    const lowered = text.toLowerCase();
    const byTitle = PRODUCT_STATUS_OPTIONS.find(
        (option) => String(option.title).toLowerCase() === lowered,
    );
    if (byTitle) {
        return byTitle.value;
    }
    return text.slice(0, PRODUCT_STATUS_MAX_LENGTH);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatProductStatus(value) {
    const key = String(value || '').trim();
    if (!key) {
        return '—';
    }
    return PRODUCT_STATUS_LABELS[key] || key;
}

/**
 * Merge predefined status options with unique custom values from items.
 * @param {Iterable<{ status?: unknown }|unknown>} [itemsOrValues]
 * @returns {Array<{ title: string, value: string }>}
 */
export function buildProductStatusFilterOptions(itemsOrValues = []) {
    const known = new Set(PRODUCT_STATUS_OPTIONS.map((option) => option.value));
    const extras = [];
    const seenExtra = new Set();
    for (const entry of itemsOrValues) {
        const raw = entry && typeof entry === 'object' && 'status' in entry
            ? entry.status
            : entry;
        const value = String(raw || '').trim();
        if (!value || known.has(value) || seenExtra.has(value)) {
            continue;
        }
        seenExtra.add(value);
        extras.push({ title: formatProductStatus(value), value });
    }
    extras.sort((a, b) => a.title.localeCompare(b.title, 'ro'));
    return [...PRODUCT_STATUS_OPTIONS, ...extras];
}

/**
 * @param {unknown} value
 * @returns {string|null}
 */
export function getProductStatusColor(value) {
    const key = String(value || '').trim();
    if (!key) {
        return null;
    }
    if (PRODUCT_STATUS_COLORS[key]) {
        return PRODUCT_STATUS_COLORS[key];
    }
    return CUSTOM_STATUS_COLOR_PALETTE[hashStatusKey(key.toLowerCase())];
}

/**
 * Inline style for colored status text (label only).
 * @param {unknown} value
 * @returns {Record<string, string>}
 */
export function getProductStatusTextStyle(value) {
    const key = String(value || '').trim();
    const color = getProductStatusColor(key);
    if (!color) {
        return {};
    }
    return {
        color,
        fontWeight: '700',
    };
}
